import React, { useEffect, useRef } from 'react';
import { parseISO, startOfDay } from 'date-fns';
import AnnouncementService from '../services/announcement-service';
import FileStorageService from '../services/file-storage-service';
import { getCurrentDateObject, toScheduledAt } from './date-helper';
import { toInputDate } from './string-format-helper';
import { toStudentUuids } from './student-uuid-helper';

const maxAttachments = 10;
const maxFileSizeInMb = 25;
const maxFileSize = maxFileSizeInMb * 1024 * 1024;

export const getAnnouncementData = (previousData?: AnnouncementDto) => {
  const data: AnnouncementFormData = {
    formData: {
      title: previousData?.title || null,
      image: previousData?.image?.url || null,
      imageFileId: previousData?.image?.fileId || null,
      description: previousData?.description || null,
      attachments: previousData?.attachments || [],
    },
    classData: [],
    studentData: [],
    unmatchedStudentUuids: [],
    scheduleDate: null,
  };

  return data;
};

export const isFormComplete = (data: AnnouncementFormData, validations: object) =>
  Boolean(
    data.formData.title &&
      data.formData.description &&
      (data.studentData.length > 0 || data.unmatchedStudentUuids.length > 0) &&
      Object.keys(validations).length === 0
  );

type SetData = React.Dispatch<React.SetStateAction<AnnouncementFormData>>;
type SetValidations = React.Dispatch<React.SetStateAction<object>>;

const setValidation = (setValidations: SetValidations, id: string, message: string | null) =>
  setValidations((current) => {
    const next = { ...current };
    if (message) next[id] = message;
    else delete next[id];
    return next;
  });

// A prévia da capa recém-enviada é uma blob: URL, que prende o arquivo inteiro
// na memória até ser revogada — e o SPA nunca descarrega a página.
const revokePreviewUrl = (url: string | null) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
};

export const useRevokePreviewUrlOnUnmount = (data: AnnouncementFormData) => {
  const image = useRef<string | null>(null);
  image.current = data.formData.image;

  useEffect(() => () => revokePreviewUrl(image.current), []);
};

export const forceRemovePreviewImage = (setData: SetData) => {
  setData((current) => {
    revokePreviewUrl(current.formData.image);

    return { ...current, formData: { ...current.formData, image: null, imageFileId: null } };
  });
};

export const deleteAttachment = (fileId: string, setData: SetData, setValidations: SetValidations) => {
  setData((current) => ({
    ...current,
    formData: {
      ...current.formData,
      attachments: current.formData.attachments.filter((attachment) => attachment.fileId !== fileId),
    },
  }));
  setValidation(setValidations, 'attachment', null);
};

export const handleStudentChange = (
  selectedClassrooms: number[],
  selectedStudents: number[],
  setData: SetData
) => {
  setData((current) => ({
    ...current,
    classData: selectedClassrooms,
    studentData: selectedStudents,
  }));
};

export interface FormChangeContext {
  data: AnnouncementFormData;
  setData: SetData;
  setValidations: SetValidations;
  setIsLoadingImage: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingAttachment: React.Dispatch<React.SetStateAction<boolean>>;
  fileStorageService: FileStorageService;
  setToastContent: (content: ToastContent) => void;
}

const uploadImage = async (
  file: File,
  { setData, setValidations, setIsLoadingImage, fileStorageService, setToastContent }: FormChangeContext
) => {
  if (file.size > maxFileSize) {
    setValidation(setValidations, 'image', `O tamanho máximo da imagem é ${maxFileSizeInMb}MB.`);
    return;
  }

  setValidation(setValidations, 'image', null);
  setIsLoadingImage(true);

  try {
    const fileId = await fileStorageService.uploadFile(file);

    // A API devolve a url assinada só depois do upload no S3 terminar; até
    // lá usamos um preview local para não travar a UX no formulário.
    const preview = URL.createObjectURL(file);
    setData((current) => {
      revokePreviewUrl(current.formData.image);

      return { ...current, formData: { ...current.formData, image: preview, imageFileId: fileId } };
    });
  } catch {
    setToastContent({ isOpen: true, text: 'Houve um erro ao enviar a imagem.', type: 'error' });
  } finally {
    setIsLoadingImage(false);
  }
};

const uploadAttachments = async (
  files: File[],
  { data, setData, setValidations, setIsLoadingAttachment, fileStorageService, setToastContent }: FormChangeContext
) => {
  if (files.some((file) => file.size > maxFileSize)) {
    setValidation(setValidations, 'attachment', `O tamanho máximo de cada anexo é ${maxFileSizeInMb}MB.`);
    return;
  }

  if (data.formData.attachments.length + files.length > maxAttachments) {
    setValidation(setValidations, 'attachment', `Quantidade máxima de anexos é ${maxAttachments}.`);
    return;
  }

  setValidation(setValidations, 'attachment', null);
  setIsLoadingAttachment(true);

  try {
    const uploaded: AnnouncementFileDto[] = await Promise.all(
      files.map(async (file) => ({ fileId: await fileStorageService.uploadFile(file), name: file.name }))
    );

    setData((current) => ({
      ...current,
      formData: { ...current.formData, attachments: current.formData.attachments.concat(uploaded) },
    }));
  } catch {
    setToastContent({ isOpen: true, text: 'Houve um erro ao enviar os anexos.', type: 'error' });
  } finally {
    setIsLoadingAttachment(false);
  }
};

// Toda atualização de estado aqui é funcional: o upload leva tempo, e o que foi
// digitado ou selecionado nesse meio-tempo não pode ser sobrescrito pelo estado
// de quando o arquivo foi escolhido.
export const handleFormOnChange = async (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  context: FormChangeContext
) => {
  const { id, value, type } = e.target;

  if (type !== 'file') {
    setValidation(context.setValidations, id, value ? null : 'Campo obrigatório.');
    context.setData((current) => ({ ...current, formData: { ...current.formData, [id]: value } }));
    return;
  }

  // O Form também chama no blur; limpar o input deixa escolher o mesmo arquivo de novo.
  if (e.type === 'blur') {
    e.target.value = null;
    return;
  }

  if (!value) return;

  const files = Array.from((e.target as HTMLInputElement).files);

  if (id === 'image') await uploadImage(files[0], context);
  else if (id === 'attachment') await uploadAttachments(files, context);
};

const handleScheduleDateSelect = (
  evt: React.ChangeEvent<HTMLInputElement>,
  data: AnnouncementFormData,
  setData: SetData,
  submitPromise: (submitData: AnnouncementFormData) => Promise<string | void>,
  setModalContent: (content: ModalContent) => any
) => {
  const scheduleDate = evt.target.value ? toScheduledAt(evt.target.value) : null;

  setData((current) => ({ ...current, scheduleDate }));

  createSetModalContent({ ...data, scheduleDate }, submitPromise, setData, setModalContent);
};

export const createSetModalContent = (
  data: AnnouncementFormData,
  submitPromise: (submitData: AnnouncementFormData) => Promise<string | void>,
  setData: SetData,
  setModalContent: (content: ModalContent) => any
) => {
  const submitSchedulePromise = (submitData: AnnouncementFormData) => {
    if (!submitData.scheduleDate) {
      return Promise.reject('Selecione uma data para o agendamento.');
    }

    const selectDate = startOfDay(parseISO(submitData.scheduleDate)).getTime();
    const currentDate = getCurrentDateObject().getTime();

    if (selectDate < currentDate) {
      return Promise.reject(
        'Não é possível agendar comunicados para datas passadas.'
      );
    } else if (selectDate === currentDate) {
      return Promise.reject(
        'Não é possível agendar comunicados para o dia atual.'
      );
    } else {
      return submitPromise(submitData);
    }
  };

  const resetScheduleDate = () => {
    setData((current) => ({ ...current, scheduleDate: null }));

    return Promise.resolve();
  };

  setModalContent({
    title: 'Selecione uma data para agendar o envio',
    modalContent: React.createElement('input', {
      type: 'date',
      name: 'schedule',
      id: 'schedule',
      min: toInputDate(new Date()),
      max: '2999-12-31',
      onChange: (evt) =>
        handleScheduleDateSelect(
          evt,
          data,
          setData,
          submitPromise,
          setModalContent
        ),
    }),
    submitButtonTitle: 'agendar',
    submitButtonType: 'primary',
    submitData: data,
    onSubmitButtonClick: submitSchedulePromise,
    onCloseModal: resetScheduleDate,
    isOpen: true,
  });
};

export const deleteAnnouncement = (
  announcementService: AnnouncementService,
  announcementId: string,
  setModalContent: (content: ModalContent) => any,
  setToastContent: (content: ToastContent) => void,
  navigate: any
) => {
  setModalContent({
    title: 'Deletar comunicado',
    modalContent: React.createElement(
      'p',
      null,
      'Deseja realmente deletar o comunicado?'
    ),
    submitButtonTitle: 'confirmar deleção',
    submitButtonType: 'primary',
    submitData: null,
    onSubmitButtonClick: () =>
      announcementService
        .deleteAnnouncement(announcementId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Comunicado deletado!',
            type: 'success',
          });
          navigate('/comunicados/listar');
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar o comunicado.',
            type: 'error',
          });
        }),
    isOpen: true,
  });
};

export const buildAnnouncementRequest = (
  data: AnnouncementFormData,
  classroomList: ClassroomStudents[]
): AnnouncementRequest => {
  const studentUuids = toStudentUuids(data.studentData, data.unmatchedStudentUuids, classroomList);

  return {
    title: data.formData.title,
    description: data.formData.description,
    scheduledAt: data.scheduleDate || null,
    imageFileId: data.formData.imageFileId || null,
    attachmentFileIds: data.formData.attachments.map((attachment) => attachment.fileId),
    studentUuids,
  };
};

// The BFF resolves student/classroom/responsible names server-side, so the engagement
// table just maps the recipients straight through — no ClassroomsWithStudents join needed.
export const backofficeRecipientsToEngagementRows = (
  recipients: BackofficeRecipientDto[]
): InfosDataListRow[][] => {
  return recipients.map((recipient) => [
    { text: recipient.student.name || '—' },
    { text: recipient.classroom?.name || '—' },
    { text: recipient.responsible?.name || '—' },
    {
      text: recipient.read ? 'Sim' : 'Não',
      customStyle: { textAlign: 'center' },
    },
  ]);
};

// The BFF already dedupes classrooms across an announcement's recipients server-side.
export const classroomsToLabel = (classrooms: { id: number; name: string }[]): string => {
  if (classrooms.length === 0) return '-';
  if (classrooms.length === 1) return classrooms[0].name;

  return `${classrooms.length} turmas`;
};
